import {useMemo} from 'react';
import {ContentSubmission} from '@/types/contentSubmission';
import campaignContentSubmissionFetchers, {
  CampaignContentSubmissionsParams,
  ContentSubmissionsListResponse,
} from '@/api/fetchers/campaignContentSubmission';
import useInfiniteReactQuery from '@/hooks/use-infinite-react-query';

const DEFAULT_STALE_TIME = 30_000;
export const CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY =
  'campaignContentSubmissions';

export type ContentSubmissionsQueryKey = [
  string, // CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY
  string, // campaignId
  string | string[] | undefined, // state
  Record<string, unknown>?, // filters object
];

type UseCampaignContentSubmissionsQueryParams = Omit<
  CampaignContentSubmissionsParams,
  'page'
> & {
  staleTime?: number;
  enabled?: boolean;
};

function useCampaignContentSubmissionsQuery({
  campaignId,
  state,
  kind,
  deliverableKind,
  q,
  contractId,
  profileId,
  domain,
  perPage,
  staleTime = DEFAULT_STALE_TIME,
  enabled = true,
  include,
  sortBy,
  sortDir,
}: UseCampaignContentSubmissionsQueryParams) {
  const response = useInfiniteReactQuery({
    queryKey: [
      CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY,
      campaignId,
      state,
      {
        kind,
        deliverableKind,
        q,
        contractId,
        profileId,
        perPage,
        domain,
        include,
        sortBy,
        sortDir,
      },
    ],
    queryFn: ({pageParam = 1}: {pageParam?: number}) =>
      campaignContentSubmissionFetchers.list({
        campaignId,
        state,
        kind,
        deliverableKind,
        q,
        contractId,
        profileId,
        perPage,
        page: pageParam,
        domain,
        include,
        sortBy,
        sortDir,
      }),
    enabled: !!campaignId && enabled,
    staleTime,
    getNextPageParam: (lastPage: ContentSubmissionsListResponse) => {
      const {pagination} = lastPage.meta;
      return pagination.currentPage < pagination.totalPages
        ? pagination.currentPage + 1
        : undefined;
    },
    structuralSharing: false,
  });

  const contentSubmissions = useMemo<ContentSubmission[]>(() => {
    if (!response.data) {
      return [];
    }
    return response.data.pages.flatMap((page) => page.data ?? []);
  }, [response.data]);

  const meta = response.data?.pages[0]?.meta;

  return {
    ...response,
    contentSubmissions,
    meta,
  };
}

export default useCampaignContentSubmissionsQuery;
